/**
 * Pack manifest schema — identity, entry point, initial inventory, seed
 * policy (plan §3).
 *
 * @remarks
 * The manifest is the pack's front door: who it is (`gameId`, `title`), what
 * schema it targets (`schemaVersion`, exact-match pre-1.0), where play begins
 * (`entryRoomId`), what the player starts holding (`initialInventory`), and
 * how the session seeds its PRNG (`seedPolicy`), which base vocabulary it
 * extends (`extendsVocabulary`, R3 ruling on D16), and what a shell shows
 * around the game (`presentation`, D132). Initial FLAG values are
 * deliberately absent — they live on each flag declaration
 * ({@link ../flags.ts | flags}), one fact in one home.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { playerCombatSchema } from './combat.js';
import { gameIdSchema, itemIdSchema, roomIdSchema } from './identifiers.js';
import { imageAspectRatioSchema } from './images.js';
import { packPresentationSchema } from './presentation.js';
import { nonBlankStringSchema, positiveIntegerSchema, randomSeedSchema } from './primitives.js';
import { contentSchemaVersionSchema } from './versioning.js';

/**
 * How a session obtains its initial PRNG seed (architecture.md §3.4):
 * `fixed` bakes the authored seed into every session (deterministic demos,
 * puzzles that depend on exact draws); `caller_supplied` lets the application
 * choose per session — and record it, because `(bundle, seed, commands)` is
 * the whole replay recipe (decision D09).
 */
export const seedPolicySchema = z
	.discriminatedUnion('type', [
		z.strictObject({
			type: z.literal('caller_supplied'),
		}),
		z.strictObject({
			seed: randomSeedSchema,
			type: z.literal('fixed'),
		}),
	])
	.readonly();

/** One seed policy — see {@link seedPolicySchema}. */
export type SeedPolicy = z.infer<typeof seedPolicySchema>;

/**
 * Where a game RUNS (D148 §7, D156): `client` ships the bundle to the
 * player's browser — offline-capable, local saves, and a pack any player can
 * read out of devtools, which is fine for a free game; `server` keeps the
 * bundle on the platform and runs every turn there (`services/play-api`),
 * so the client is words in, text out and holds no flag. Required for a paid
 * game, available to a free one (platform.md §2). There is no middle option
 * — a "spoiler-safe" client bundle is a server turn with extra steps.
 */
export const playModeSchema = z.enum(['client', 'server']);

/** One play mode — see {@link playModeSchema}. */
export type PlayMode = z.infer<typeof playModeSchema>;

/**
 * D179 (Mike's ruling, 2026-09-15): whether the game saves itself or the
 * player must. `automatic` — every turn is kept, and `SAVE` / `LOAD` answer
 * that there is nothing to do (the demo's choice: "we already offer a cheat
 * with saving"). `manual` — the game keeps nothing until the player says
 * `SAVE`, and `LOAD` brings that back; the one slot, no naming, no warning
 * on the way out — the experience is a later round's, the field is this
 * one's. One `SAVE` directive whose answer depends on the mode (§2.1), not
 * a directive some packs have and others lack — and HELP lists it only
 * where it does something.
 */
export const saveModeSchema = z.enum(['automatic', 'manual']);

/** One save mode — see {@link saveModeSchema}. */
export type SaveMode = z.infer<typeof saveModeSchema>;

/** The pack manifest — validated from `config/game.config.json`. */
export const packManifestSchema = z
	.strictObject({
		/**
		 * R25: the carry capacity, in units of item `bulk` (absent = unlimited,
		 * so no existing pack changes behavior). Built as a primitive; the POC
		 * deliberately authors none (D95: a limit over four portables is a
		 * chore, not a choice).
		 */
		carryCapacity: positiveIntegerSchema.optional(),
		entryRoomId: roomIdSchema,
		/**
		 * The base vocabulary this pack EXTENDS (decision D16; R3 ruling,
		 * 2026-08-13): the registry key of a reference vocabulary pack (e.g.
		 * `standard-english-vocabulary`) that the loader resolves from its
		 * caller-supplied registry and merges UNDER this pack's own vocabulary.
		 * ABSENCE is authored intent — the pack's vocabulary stands alone. A
		 * declared base the caller cannot resolve is a hard load failure, never
		 * a silent degradation: the base is part of the bundle's identity, which
		 * is where replay determinism needs it.
		 */
		extendsVocabulary: nonBlankStringSchema.optional(),
		gameId: gameIdSchema,
		/**
		 * R23 (D89): the pack's ONE plate aspect ratio (`width:height`), so the
		 * layout never jumps between rooms. Optional here for shape, but the
		 * pack-level refinement requires it whenever the pack declares images —
		 * layout stability as a validation, not a hope.
		 */
		imageAspectRatio: imageAspectRatioSchema.optional(),
		initialInventory: z.array(itemIdSchema).readonly().default([]),
		/** D156: where the game runs — see {@link playModeSchema}. Absent is `client`, which is every pack before D156. */
		play: playModeSchema.default('client'),
		/**
		 * D234: the player's body — health, bare hands, and the ending a death
		 * selects (`combat.ts`). Absent, the pack has no combat and pays
		 * nothing; the loader refuses a foe in a pack that declares none, and a
		 * body here in a pack with no foe (a health nothing can lower).
		 */
		player: playerCombatSchema.optional(),
		/**
		 * D132: what a shell shows around the game — logo, subtitle, title
		 * screen. Optional; the loader fills every gap from its built-in table,
		 * so absence is a plain title screen rather than a broken one.
		 */
		presentation: packPresentationSchema.optional(),
		/** D179: who saves — see {@link saveModeSchema}. Absent is `automatic`, which is every pack before D179. */
		saves: saveModeSchema.default('automatic'),
		schemaVersion: contentSchemaVersionSchema,
		seedPolicy: seedPolicySchema,
		title: nonBlankStringSchema,
	})
	.readonly();

/** One pack manifest — see {@link packManifestSchema}. */
export type PackManifest = z.infer<typeof packManifestSchema>;
